/**
 * <s-catalog-search> — inline catalog search with live suggestions.
 *
 * Uses Shopify's Predictive Search API (`/search/suggest.json`), which already
 * applies Shopify's typo tolerance. Query suggestions returned by Shopify are
 * offered as "¿Quizás buscabas …?" only when they differ from what was typed;
 * nothing is invented client-side. The typed text is never cleared.
 */
const DEBOUNCE_MS = 250;
const PRODUCT_LIMIT = 6;

class SCatalogSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.panel = this.querySelector('[data-search-panel]');
    this.status = this.querySelector('[data-search-status]');
    if (!this.input || !this.panel) return;

    this.timer = null;
    this.controller = null;
    this.lastTerm = '';

    this.onInput = () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), DEBOUNCE_MS);
    };
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onFocusIn = () => {
      if (this.panel.childElementCount && this.input.value.trim()) this.open();
    };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    };
    this.onDocClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.input.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.input?.removeEventListener('input', this.onInput);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('click', this.onDocClick);
  }

  async search() {
    const term = this.input.value.trim();
    if (!term) {
      this.lastTerm = '';
      this.panel.replaceChildren();
      this.close();
      return;
    }
    if (term === this.lastTerm && this.panel.childElementCount) {
      this.open();
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,query',
      'resources[limit]': String(PRODUCT_LIMIT),
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });

    try {
      const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`, {
        signal: this.controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      // Ignore stale responses if the field changed meanwhile.
      if (this.input.value.trim() !== term) return;
      this.lastTerm = term;
      this.render(term, data?.resources?.results ?? {});
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError();
    }
  }

  render(term, results) {
    const products = Array.isArray(results.products) ? results.products : [];
    const queries = Array.isArray(results.queries) ? results.queries : [];
    const normalized = term.toLocaleLowerCase();
    const alternative = queries.find((query) => query?.text && query.text.toLocaleLowerCase() !== normalized);

    const fragment = document.createDocumentFragment();

    if (alternative && (products.length === 0 || !this.matchesAnyTitle(products, normalized))) {
      fragment.append(this.buildDidYouMean(alternative.text));
    }

    if (products.length) {
      const list = document.createElement('ul');
      list.className = 'grid gap-1';
      list.setAttribute('role', 'list');
      for (const product of products) list.append(this.buildProduct(product));
      fragment.append(list);
      fragment.append(this.buildViewAll(term));
      this.announce(`${products.length} ${this.dataset.resultsLabel}`);
    } else {
      fragment.append(this.buildEmpty());
      this.announce(this.dataset.noResults);
    }

    this.panel.replaceChildren(fragment);
    this.open();
  }

  matchesAnyTitle(products, normalized) {
    return products.some((product) => String(product?.title ?? '').toLocaleLowerCase().includes(normalized));
  }

  buildDidYouMean(text) {
    const row = document.createElement('p');
    row.className = 'type-body m-0 px-3 py-2 text-on-surface';
    row.append(`${this.dataset.didYouMean} `);
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'cursor-pointer font-semibold text-interactive underline underline-offset-4 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive rounded-sm';
    button.textContent = text;
    button.addEventListener('click', () => {
      this.input.value = text;
      this.input.focus();
      this.search();
    });
    row.append(button, '?');
    return row;
  }

  buildProduct(product) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = product.url;
    link.className =
      'flex items-center gap-3 rounded-card p-2 text-on-surface no-underline hover:bg-surface-secondary focus-visible:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-interactive';

    const media = document.createElement('span');
    media.className = 'block size-16 shrink-0 overflow-hidden rounded-card bg-surface-secondary';
    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = this.sizedImage(imageUrl, 160);
      image.alt = '';
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      image.className = 'size-full object-cover';
      media.append(image);
    }

    const text = document.createElement('span');
    text.className = 'flex min-w-0 flex-col gap-0.5';
    const title = document.createElement('span');
    title.className = 'type-body font-semibold leading-snug line-clamp-2';
    title.textContent = product.title;
    text.append(title);

    const price = this.formatPrice(product);
    if (price) {
      const priceEl = document.createElement('span');
      priceEl.className = 'text-sm text-on-surface-secondary';
      priceEl.textContent = price;
      text.append(priceEl);
    }

    link.append(media, text);
    item.append(link);
    return item;
  }

  buildViewAll(term) {
    const link = document.createElement('a');
    link.href = `${this.dataset.searchUrl}?${new URLSearchParams({ q: term, 'options[prefix]': 'last' })}`;
    link.className =
      'mt-2 flex min-h-11 items-center justify-center rounded-button border border-border px-4 text-sm font-semibold text-on-surface no-underline hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive';
    link.textContent = this.dataset.viewAll;
    return link;
  }

  buildEmpty() {
    const wrap = document.createElement('div');
    wrap.className = 'grid gap-3 px-3 py-3';
    const message = document.createElement('p');
    message.className = 'type-body m-0 text-on-surface';
    message.textContent = this.dataset.noResults;
    const link = document.createElement('a');
    link.href = this.dataset.collectionsUrl;
    link.className =
      'justify-self-start inline-flex min-h-11 items-center rounded-button bg-interactive px-5 text-sm font-semibold text-on-interactive no-underline hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive';
    link.textContent = this.dataset.collectionsLabel;
    wrap.append(message, link);
    return wrap;
  }

  renderError() {
    const message = document.createElement('p');
    message.className = 'type-body m-0 px-3 py-2 text-on-surface-secondary';
    message.textContent = this.dataset.errorMessage;
    this.panel.replaceChildren(message);
    this.open();
  }

  formatPrice(product) {
    const min = Number.parseFloat(product.price_min ?? product.price);
    const max = Number.parseFloat(product.price_max ?? product.price);
    if (!Number.isFinite(min)) return '';
    let formatted;
    try {
      formatted = new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: this.dataset.currency,
      }).format(min);
    } catch {
      formatted = String(min);
    }
    return Number.isFinite(max) && max > min ? `${this.dataset.priceFrom} ${formatted}` : formatted;
  }

  sizedImage(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (!this.panel.hidden) {
        event.preventDefault();
        this.close();
        this.input.focus();
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const focusables = [...this.panel.querySelectorAll('a, button')];
    if (!focusables.length || this.panel.hidden) return;
    event.preventDefault();
    const index = focusables.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      focusables[index + 1 < focusables.length ? index + 1 : 0].focus();
    } else if (index <= 0) {
      this.input.focus();
    } else {
      focusables[index - 1].focus();
    }
  }

  announce(text) {
    if (this.status) this.status.textContent = text;
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('s-catalog-search')) customElements.define('s-catalog-search', SCatalogSearch);
